import { isDebugTs, isDebugTopAdTs } from './log';
import adSettings from '../config/adsettings';
import { pbjs } from '../prebid/index';
import { getCookie } from '../storage/cookies';
import { getModules } from '../modules/index';
import { isSubDomain, getSiteName } from '../config/domain';
import amaLog, { amaWarn } from './log';
import { timeFor } from './time';
////////////////////
///////////////////////////////////////////////////////////
/////////

export const isNativeAd = (ad: Ad): boolean => ad.placement.includes('native');

export const isNotNativeAd = (ad: Ad): boolean => !isNativeAd(ad);

export const adHasExistingContainer = (ad: Ad): boolean =>
  document.getElementById(`${ad.target}-${adSettings.adsUniqueId}`) !== null;

export const capitalizeFirst = (content: string): string =>
  (content && content[0].toUpperCase() + content.slice(1)) || '';

export const isDebug = (): boolean => isDebugTs();

export const isDebugTopAd = (): boolean => isDebugTopAdTs;

export const once = (func: Function): Function => {
  let result: any;

  return () => {
    if (!result) {
      result = func();
    }
    return result;
  };
};

export const stripDashAndAfter = (str: string): string => {
  const dashIndex = str.indexOf('-');
  return dashIndex >= 0 ? str.substring(0, dashIndex) : str;
};

export const throttle = (fn: Function, duration: number): Function => {
  let suspended = false;
  return () => {
    if (!suspended) {
      suspended = true;
      setTimeout(() => {
        fn();
        suspended = false;
      }, duration);
    }
  };
};

export const random = (min: number, max: number): number => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const isElementInViewport = (element: Element, offset: number): boolean => {
  let isWithin = false;
  if (element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    isWithin = rect.top <= windowHeight + offset && rect.top + rect.height >= 0;
  }
  return isWithin;
};

/**
 * Triggers callback when slot element enters viewport boundary.
 */
const observeSlotForLazyload = (
  element: HTMLElement,
  offset: number = 100,
  onIntersect: () => void
): void => {
  if (!('IntersectionObserver' in window)) {
    onIntersect();
    return;
  }

  let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add a tiny dwell check (150ms) to ensure user didn't fast-scroll past
          scrollTimeout = setTimeout(() => {
            obs.disconnect();
            onIntersect();
          }, 150);
        } else if (scrollTimeout) {
          // User scrolled past quickly before dwell timer completed
          clearTimeout(scrollTimeout);
          scrollTimeout = null;
        }
      });
    },
    {
      rootMargin: `${offset}px 0px ${offset}px 0px`,
      threshold: 0.1,
    }
  );

  observer.observe(element);
};

/**
 * find out without lazyload banners
 *
 * @param {object} ad
 * @return boolean
 */
export function lazyloadSlot(ad: IAdRaw, pageNo = 1): boolean {
  const { useGLazyLoad } = getModules();
  return (useGLazyLoad && (pageNo > 1 || !useGLazyLoad.includes(ad.placement)));
}

const applyPoliticalAdCssFixes = (adElement: HTMLElement, slotId: string): void => {
  // Keep slot constrained and mark it for any later scaling branch checks.
  adElement.style.width = '100vw';

  // Creative iframe can be injected slightly later, so retry briefly.
  let attempts = 0;
  const maxAttempts = 20;
  const timer: ReturnType<typeof setInterval> = setInterval(() => {
    attempts++;
    const iframe = adElement.querySelector('iframe') as HTMLIFrameElement | null;

    if (iframe) {
      iframe.style.display = 'block';
      iframe.style.maxWidth = '100vw';
      iframe.style.border = '0';
      iframe.setAttribute('scrolling', 'no');
      iframe.setAttribute('marginwidth', '0');
      iframe.setAttribute('marginheight', '0');
      iframe.setAttribute('frameborder', '0');

      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc?.body && iframeDoc?.documentElement) {
          iframeDoc.body.style.margin = '0';
          iframeDoc.body.style.padding = '0';
          iframeDoc.documentElement.style.margin = '0';
          iframeDoc.documentElement.style.padding = '0';
          amaLog(`Political ads: normalized iframe body/html margins for slot ${slotId}.`, attempts);
        } else {
          amaLog(`Political ads: iframe document not ready for slot ${slotId}.`);
        }
      } catch (error) {
        amaLog(`Political ads: iframe not accessible for margin reset in slot ${slotId}.`);
      }

      clearInterval(timer);
      return;
    }

    if (attempts >= maxAttempts) {
      amaLog(`Political ads: no iframe found for slot ${slotId} after ${maxAttempts} attempts.`);
      clearInterval(timer);
    }
  }, 150);
};

const checkSlotPoliticalAds = (slotElementId: string): { 
  status: 'ELIGIBLE' | 'NOT_ELIGIBLE' | 'NO_BIDS'; 
} => {
  try {
    if (typeof window.pbjs !== 'undefined' && typeof window.pbjs.getBidResponses === 'function') {
      const bidResponses = window.pbjs.getBidResponsesForAdUnitCode(slotElementId);

      if (!bidResponses || !bidResponses.bids || bidResponses.bids.length === 0) {
        return { status: 'NO_BIDS' };
      }
      const winningDrBid = bidResponses.bids.find(
        (bid: any) => bid.directRender && typeof bid.directRender.rule === 'string'
      );

      if (winningDrBid) {
        amaLog(`Political ads: Direct Render rule match caught. Bidder: ${winningDrBid.bidderCode}`);
        return { status: 'ELIGIBLE' };
      }
    }
  } catch (error) {
    console.error('Political ads: Error evaluating directRender bidder flag:', error);
  }
  return { status: 'NOT_ELIGIBLE' };
};

/**
 * Helps us refresh an ad with Prebid
 *
 * @param {object} ad
 * @return void
 */
export function refreshPrebidAd(ad: IAdRaw, afterRefresh?: () => void, usePoliticalAds: boolean = false): void {
  refreshPrebidAds([ad], afterRefresh, usePoliticalAds);
}

const FAILSAFE_TIMEOUT_MS = 2000;

export function refreshPrebidAds(ads: IAdRaw[], afterRefresh?: () => void, usePoliticalAds: boolean = false): void {
  const validAds = ads.filter((ad) => ad?.adSlot);
  if (!validAds.length) return;

  const executeAdsRefresh = (adsToRefresh: IAdRaw[]): void => {
    const validSlots = adsToRefresh.map((ad) => ad.adSlot!);
    const slotIds = validSlots.map((slot) => slot.getSlotElementId());
    let done = false;

    const refreshByPolicy = (source: 'callback' | 'failsafe'): void => {
      if (!usePoliticalAds) {
        if (source === 'callback') {
          amaLog(`Political ads: Refreshing all slots immediately via GPT.`, validSlots);
        }
        window.googletag.pubads().refresh(validSlots, { changeCorrelator: false });
        afterRefresh?.();
        return;
      }

      const politicalSlots: googletag.Slot[] = [];
      const nonPoliticalSlots: googletag.Slot[] = [];

      validSlots.forEach((slot) => {
        const slotId = slot.getSlotElementId();
        const { status } = checkSlotPoliticalAds(slotId);
        if (status === 'ELIGIBLE') {
          politicalSlots.push(slot);
        } else {
          nonPoliticalSlots.push(slot);
        }
      });

      // 1. Refresh non-political slots via GPT
      if (nonPoliticalSlots.length) {
        amaLog(`Political ads: Refreshing non-political ads via GPT (${nonPoliticalSlots.length})`);
        window.googletag.pubads().refresh(nonPoliticalSlots, { changeCorrelator: false });
      }

      // 2. Render political slots direct via Prebid (skip GPT)
      if (politicalSlots.length) {
        politicalSlots.forEach((adSlot) => {
          const slotId = adSlot.getSlotElementId();
          const adElement = document.getElementById(slotId);

          if (!adElement) return;

          const bidResponses = window.pbjs.getBidResponsesForAdUnitCode?.(slotId);
          const winningBid = bidResponses?.bids?.find(
            (bid: any) => bid.directRender && typeof bid.directRender.rule === 'string'
          );

          if (winningBid) {
            amaLog(`Political ads: Rendering political ad direct via Prebid for slot ${slotId}`);
            const iframe = document.createElement('iframe');
            adElement.innerHTML = '';
            adElement.appendChild(iframe);

            const iframeDoc = iframe.contentWindow?.document;
            if (iframeDoc && typeof window.pbjs.renderAd === 'function') {
              window.pbjs.renderAd(iframeDoc, winningBid.adId);
            }

            applyPoliticalAdCssFixes(adElement, slotId);
          }
        });
      }

      afterRefresh?.();
    };

    pbjs.que.push((): void => {
      const startedAt = Date.now();
      const failsafe = setTimeout(() => {
        if (done) return;
        done = true;
        amaWarn(`refreshPrebidAds failsafe fired after ${Date.now() - startedAt}ms`, { slotIds });
////////////////////////////
////////////////////////////////////////////////////////////////////////////
/////////////////
        refreshByPolicy('failsafe');
      }, FAILSAFE_TIMEOUT_MS);

      amaLog(`pbjs.rp.requestBids for batch at ${timeFor(validSlots.join())}`, { slotIds });
      slotIds.forEach((slotId) => {
////////////////////////////
////////////////////////////////////////////////
/////////////////
        timeFor(slotId);
      });

      pbjs.rp.requestBids({
        gptSlotObjects: validSlots,
        callback: () => {
          if (done) return;
          done = true;
          clearTimeout(failsafe);

          slotIds.forEach((slotId) => {
////////////////////////////////
///////////////////////////////////////////////////
/////////////////////
            amaLog(`pbjs.rp.requestBids for ${slotId} completed after ${timeFor(slotId)}`);
          });
          refreshByPolicy('callback');
        },
      });
    });
  };

  if (usePoliticalAds) {
    // Attach observer upfront per slot BEFORE initiating Prebid auction
    validAds.forEach((ad) => {
      const slotId = ad.adSlot!.getSlotElementId();
      const adElement = document.getElementById(slotId);

      if (adElement) {
        const site = getSiteName();
        const offset = site === 'hant' ? 500 : 100; // Adjust offset as needed for political ads
        amaLog(`Political ads offset value ${offset}`);
        observeSlotForLazyload(adElement, offset, () => {
          amaLog(`Political ads observer triggered for slot ${slotId}. Initiating auction.`);
          executeAdsRefresh([ad]);
        });
      } else {
        executeAdsRefresh([ad]);
      }
    });
  } else {
    executeAdsRefresh(validAds);
  }
}

export const clone = (obj: any): any => typeof structuredClone === 'function' ? structuredClone(obj) : JSON.parse(JSON.stringify(obj));

export const isLocal = () => window.location.hostname === 'localhost' || window.location.hostname.includes('labdevs');

export const isNil = (val: any) => typeof val === 'undefined' || val === null;

export const isNotNil = (val: any) => !isNil(val);

export const isString = (val: any) => typeof val === 'string';

export const stringIfAny = (id: string | number) => (typeof id !== 'undefined' ? id.toString() : undefined);

export const isValidTargeting = (trg: ITargeting): boolean => {
  return (
    isNotNil(trg) &&
    isNotNil(trg.key) &&
    isNotNil(trg.value) &&
    isString(trg.key) &&
    (isString(trg.value) || (Array.isArray(trg.value) && trg.value.every(isString)))
  );
};

export const googleTagIsReady = () => !!(window.googleTagInitialized && window.googleTagInitialized === true);

// Function to check if a value is empty
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) {
    return true;
  }

  if (Array.isArray(value) || typeof value === 'string') {
    return value.length === 0;
  }

  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false; // For other types, consider them not empty
};

export const pluck = (property: string, array: ObjectType[]): any[] => {
  return array.map((obj) => obj[property]);
};

export const isBlogSite: boolean =
  window.location.hostname.replace(/^(www\.)|^(www-stage\.)|^([^\.]+-env\.)/gm, '').split('.').length > 2;

export const setStyle = (element: HTMLElement | null, styles: { [key: string]: string }): void => {
  if (!element) return;

  const currentStyles = element.getAttribute('style') || '';
  const newStyles = Object.entries(styles)
    .map(([property, value]) => `${property}:${value};`)
    .join(' ');

  element.setAttribute('style', `${currentStyles} ${newStyles.trim()}`);
};

let loginIdCache: string | undefined = undefined;

export const getLoginId = () => {
  if (loginIdCache !== undefined) return loginIdCache;

  const loginToken = getCookie('login_token');
  let ccValue;

  if (loginToken) {
    try {
      const payload = JSON.parse(atob(loginToken.split('.')[1]));
      ccValue = payload.data?.cc;
    } catch (error) {
      console.error('Error processing login_token:', error);
    }
  }
  loginIdCache = ccValue;
  return loginIdCache;
};

export const isMainAndCategoryPage = (): boolean => {
  if (isSubDomain()) return false;
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);

  if (path === '/') return true;

  return segments.length === 1;
};

export default {
  refreshPrebidAd,
  googleTagIsReady,
  isNil,
  isNotNil,
  isString,
  lazyloadSlot,
  isEmpty,
  pluck,
  getLoginId,
  isMainAndCategoryPage,
};